import React, { useEffect, useState } from "react";
import api from "../lib/api";
import PriceChart from "../components/PriceChart";
import { FlashCell, fmtPrice, fmtPct, fmtMoney } from "../components/Format";
import { Input } from "../components/ui/input";
import { Search } from "lucide-react";

export default function Markets() {
  const [ticks, setTicks] = useState([]);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState("BTC");
  const [filter, setFilter] = useState("all");

  async function refresh() {
    try {
      const r = await api.get("/markets");
      setTicks(r.data);
    } catch { /* ignore transient refresh failures */ }
  }
  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 1500);
    return () => clearInterval(id);
  }, []);

  const filtered = ticks.filter((t) => {
    if (filter !== "all" && t.asset_class !== filter) return false;
    if (query && !t.symbol.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  });

  const selectedTick = ticks.find((t) => t.symbol === selected);

  return (
    <div className="space-y-6" data-testid="markets-page">
      <div>
        <div className="label-xs">Live Feed</div>
        <h1 className="text-3xl font-[Chivo] tracking-tight">Markets</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 border border-border bg-[#0F0F0F] flex flex-col">
          <div className="p-3 border-b border-border space-y-3">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
              <Input
                data-testid="markets-search"
                placeholder="Search symbol…"
                className="bg-[#0A0A0A] border-border pl-9 focus:ring-1 focus:ring-[#007AFF]"
                value={query} onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <div className="flex gap-1" data-testid="market-filter">
              {["all", "crypto", "stock"].map((f) => (
                <button
                  key={f}
                  data-testid={`filter-${f}`}
                  onClick={() => setFilter(f)}
                  className={`flex-1 text-xs font-[Chivo] tracking-wider py-1.5 rounded-sm border ${
                    filter === f
                      ? "bg-[#007AFF] text-white border-[#007AFF]"
                      : "border-border text-neutral-400 hover:text-white"
                  }`}
                >
                  {f.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
          <div className="overflow-y-auto" style={{ maxHeight: 600 }}>
            <table className="w-full text-sm" data-testid="markets-table">
              <thead className="sticky top-0 bg-[#0F0F0F]">
                <tr className="border-b border-border">
                  <th className="text-left px-3 py-2 label-xs">Symbol</th>
                  <th className="text-right px-3 py-2 label-xs">Price</th>
                  <th className="text-right px-3 py-2 label-xs">24h</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((t) => (
                  <tr
                    key={t.symbol}
                    data-testid={`market-row-${t.symbol}`}
                    onClick={() => setSelected(t.symbol)}
                    className={`border-b border-border cursor-pointer hover:bg-[#141414] ${
                      selected === t.symbol ? "bg-[#141414]" : ""
                    }`}
                  >
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <span className={`w-1.5 h-1.5 rounded-full ${t.asset_class === "crypto" ? "bg-[#FFB300]" : "bg-[#007AFF]"}`} />
                        <span className="font-[Chivo]">{t.symbol}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right num">
                      <FlashCell value={t.price} format={fmtPrice} />
                    </td>
                    <td className={`px-3 py-2 text-right num ${t.change_pct_24h >= 0 ? "profit" : "loss"}`}>
                      {fmtPct(t.change_pct_24h)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="lg:col-span-7 border border-border bg-[#0F0F0F]" data-testid="market-detail">
          {selectedTick && (
            <>
              <div className="px-5 py-4 border-b border-border flex items-center justify-between">
                <div>
                  <div className="text-2xl font-[Chivo]">{selectedTick.symbol}</div>
                  <div className="label-xs">{selectedTick.asset_class}</div>
                </div>
                <div className="text-right">
                  <div className="text-2xl num"><FlashCell value={selectedTick.price} format={(v) => `$${fmtPrice(v)}`} /></div>
                  <div className={`num text-sm ${selectedTick.change_pct_24h >= 0 ? "profit" : "loss"}`}>{fmtPct(selectedTick.change_pct_24h)}</div>
                </div>
              </div>
              <PriceChart symbol={selected} height={340} />
              <div className="grid grid-cols-3 border-t border-border">
                <Stat label="Bid" value={`$${fmtPrice(selectedTick.bid)}`} />
                <Stat label="Ask" value={`$${fmtPrice(selectedTick.ask)}`} />
                <Stat label="24h Volume" value={`$${fmtMoney(selectedTick.volume_24h, 0)}`} />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="px-5 py-4 border-r border-border last:border-r-0">
      <div className="label-xs">{label}</div>
      <div className="num text-sm mt-1">{value}</div>
    </div>
  );
}
